import React, { SyntheticEvent, useState } from 'react'
import { DropzoneArea } from 'material-ui-dropzone'
import { Phrases } from 'constants/phrases'
import { OrganizationDTO } from '../dtos/OrganizationDTO'
import HeaderWithIcon from './HeaderWithIcon'
import OrganizationInfoIcon from 'images/white-label/organization-info.png'

interface Props {
  title: string
  formData: Partial<OrganizationDTO>
  onChange: (formData: Partial<OrganizationDTO>, shouldSkipDirty?: boolean) => void
  onSubmit: (formData: Partial<OrganizationDTO> | FormData) => void
  errors: Partial<Record<keyof OrganizationDTO, Array<string>>>
  isSub?: boolean
  isEdit?: boolean
}
const MEGA_BYTES_10 = 10485760

const portalTypeOptions = [
  { label: 'EXEC Unlimited', value: 'exec_unlimited' },
  { label: 'CCo Brand', value: 'cco_brand' },
  { label: 'EXEC', value: 'exec' },
  { label: 'EXEC White Label', value: 'white_label' },
  { label: 'EU Executive', value: 'eu_executive' },
  { label: 'EU Premium', value: 'eu_premium' },
  { label: 'EU Agency', value: 'eu_agency' },
]

const portalStatusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Cancelled', value: 'cancelled' },
]

const OrganizationForm: React.FC<Props> = ({ title, formData, onChange, onSubmit, isSub, errors, isEdit }) => {
  const handleSubmit = (event: SyntheticEvent) => {
    const fd = new FormData()
    if (formData.name) fd.append('name', formData.name)
    if (formData.subdomain) fd.append('subdomain', formData.subdomain)
    if (formData.website) fd.append('website', formData.website)
    if (formData.logo) fd.append('logo', formData.logo)
    if (formData.login_image) fd.append('login_image', formData.login_image)
    if (formData.chargify_subdomain) fd.append('chargify_subdomain', formData.chargify_subdomain)
    if (formData.description) fd.append('description', formData.description)
    if (formData.portal_type) fd.append('portal_type', formData.portal_type)
    if (formData.portal_status) fd.append('portal_status', formData.portal_status)
    if (formData.external_url !== undefined || formData.external_url !== null) {
      fd.append('external_url', formData.external_url as string)
    }
    fd.append('active_campaign_enabled', String(formData.active_campaign_enabled === true))
    fd.append('active_campaign_insider_enabled', String(formData.active_campaign_insider_enabled === true))
    fd.append('paid_by_organization', String(formData.paid_by_organization === true))
    fd.append('marketplace_offers_only', String(formData.marketplace_offers_only === true))
    if (formData.main_subscription_id) fd.append('main_subscription_id', formData.main_subscription_id)
    fd.append('hide_sub_accounts_tab', String(formData.hide_sub_accounts_tab === true))
    fd.append('hide_payments_tab', String(formData.hide_payments_tab === true))

    event.preventDefault()
    onSubmit(fd)
  }

  const [logoErrors, setLogoErrors] = useState<string[]>([])
  const [heroImageErrors, setHeroImageErrors] = useState<string[]>([])

  return (
    <form onSubmit={handleSubmit} autoComplete='off'>
      <div className='columns is-justify-content-center is-0'>
        <div className='column p-0'>
          <div className='mb-6 mt-6'>
            <HeaderWithIcon
              iconUrl={OrganizationInfoIcon}
              title={title}
              description='Basic information about your organization'
            />
          </div>
          <div className='columns is-flex mb-6'>
            <div className='column field'>
              <label>Organization name*</label>
              <div className='control'>
                <input
                  autoComplete='none'
                  className='input'
                  required
                  value={formData.name}
                  onChange={(event) => onChange({ ...formData, name: event.target.value })}
                  placeholder='Enter your organization name'
                />
              </div>
              <p className='help'>This name will appear throughout your platform</p>
            </div>
            <div className='column field'>
              <label>Subdomain</label>
              <div className='control'>
                <input
                  type='text'
                  className='input'
                  placeholder='Your company'
                  value={formData.subdomain}
                  onChange={(event) => onChange({ ...formData, subdomain: String(event.target.value).toLowerCase() })}
                />
              </div>
              <p className='help'>Your custom URL where members will access the platform</p>
              {errors?.subdomain &&
                errors?.subdomain.map((message) => (
                  <p className='help is-danger' key={message}>
                    {message}
                  </p>
                ))}
            </div>
          </div>
          <div className='field mb-6'>
            <label>Custom Domain</label>
            <div className='control'>
              <input
                type='text'
                className='input'
                placeholder='www.yourcompany.com'
                value={formData.external_url}
                onChange={(event) => onChange({ ...formData, external_url: event.target.value })}
              />
            </div>
            <p className='help'>
              Your custom domain where members will access the platform (addition DNS configuration required)
            </p>
            {errors?.external_url &&
              errors?.external_url.map((message) => (
                <p className='help is-danger' key={message}>
                  {message}
                </p>
              ))}
          </div>
          <div className='field mb-6'>
            <label>Description</label>
            <div className='control'>
              <textarea
                className='textarea'
                placeholder='Brief description of your organization'
                value={formData.description}
                onChange={(event) => onChange({ ...formData, description: event.target.value })}
              />
            </div>
            <p className='help'>Optional company summary that may appear in member-facing areas</p>
          </div>
          <div className='field mb-4'>
            <div className='control'>
              <label className='checkbox'>
                <input
                  name='hide_sub_accounts_tab'
                  type='checkbox'
                  checked={formData.hide_sub_accounts_tab}
                  onChange={(event) => onChange({ ...formData, hide_sub_accounts_tab: event.target.checked })}
                />
                Hide sub accounts tab
              </label>
            </div>
            {errors?.hide_sub_accounts_tab &&
              errors?.hide_sub_accounts_tab?.map((message: string) => (
                <p className='help is-danger' key={message.length}>
                  {message}
                </p>
              ))}
          </div>
          <div className='field mb-4'>
            <div className='control'>
              <label className='checkbox'>
                <input
                  name='hide_payments_tab'
                  type='checkbox'
                  checked={formData.hide_payments_tab}
                  onChange={(event) => onChange({ ...formData, hide_payments_tab: event.target.checked })}
                />
                Hide payments tab
              </label>
            </div>
            {errors?.hide_payments_tab &&
              errors?.hide_payments_tab?.map((message: string) => (
                <p className='help is-danger' key={message.length}>
                  {message}
                </p>
              ))}
          </div>
          {!isSub && (
            <>
              <div className='field mb-6'>
                <label>Website URL*</label>
                <div className='control'>
                  <input
                    type='text'
                    className='input'
                    placeholder='https://www.yourcompany.com'
                    required
                    value={formData.website}
                    onChange={(event) => onChange({ ...formData, website: event.target.value })}
                  />
                </div>
                <p className='help'>Link to your main company website</p>
                {errors?.website &&
                  errors?.website.map((message) => (
                    <p className='help is-danger' key={message}>
                      {message}
                    </p>
                  ))}
              </div>
              <div className='field mb-4'>
                <div className='control'>
                  <label className='checkbox'>
                    <input
                      name='paid_by_organization'
                      type='checkbox'
                      checked={formData.paid_by_organization}
                      onChange={(event) => onChange({ ...formData, paid_by_organization: event.target.checked })}
                    />
                    Paid by organization
                  </label>
                </div>
                {errors?.paid_by_organization &&
                  errors?.paid_by_organization?.map((message: string) => (
                    <p className='help is-danger' key={message.length}>
                      {message}
                    </p>
                  ))}
              </div>
              <div className='field mb-6'>
                <div className='control'>
                  <label className='checkbox'>
                    <input
                      name='marketplace_offers_only'
                      type='checkbox'
                      checked={formData.marketplace_offers_only}
                      onChange={(event) => onChange({ ...formData, marketplace_offers_only: event.target.checked })}
                    />
                    Marketplace offers only
                  </label>
                </div>
                {errors?.marketplace_offers_only &&
                  errors?.marketplace_offers_only?.map((message: string) => (
                    <p className='help is-danger' key={message.length}>
                      {message}
                    </p>
                  ))}
              </div>
              <div className='field mb-6'>
                <div className='field'>
                  <div className='control'>
                    <label className='checkbox'>
                      <input
                        name='active_campaign_enabled'
                        type='checkbox'
                        checked={formData.active_campaign_enabled}
                        onChange={(event) => onChange({ ...formData, active_campaign_enabled: event.target.checked })}
                      />
                      Active Campaign enabled
                    </label>
                  </div>
                  {errors?.active_campaign_enabled &&
                    errors.active_campaign_enabled?.map((message: string) => (
                      <p className='help is-danger' key={message.length}>
                        {message}
                      </p>
                    ))}
                </div>
                <div className='field'>
                  <div className='control'>
                    <label className='checkbox'>
                      <input
                        name='active_campaign_insider_enabled'
                        type='checkbox'
                        checked={formData.active_campaign_insider_enabled}
                        onChange={(event) =>
                          onChange({ ...formData, active_campaign_insider_enabled: event.target.checked })
                        }
                      />
                      Active Campaign Insider enabled
                    </label>
                  </div>
                  {errors?.active_campaign_insider_enabled &&
                    errors.active_campaign_insider_enabled?.map((message: string) => (
                      <p className='help is-danger' key={message.length}>
                        {message}
                      </p>
                    ))}
                </div>
              </div>
              <div className='field'>
                <label>Portal Type</label>
                <div className='control'>
                  <div className='select is-fullwidth'>
                    <select
                      required
                      value={formData.portal_type || ''}
                      onChange={(event) => onChange({ ...formData, portal_type: event.target.value })}
                    >
                      <option value=''>Select Portal Type</option>
                      {portalTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {errors?.portal_type &&
                  errors?.portal_type.map((message) => (
                    <p className='help is-danger' key={message}>
                      {message}
                    </p>
                  ))}
              </div>
              <div className='field mb-6'>
                <label>Portal Status</label>
                <div className='control'>
                  <div className='select is-fullwidth'>
                    <select
                      required
                      value={formData.portal_status || ''}
                      onChange={(event) => onChange({ ...formData, portal_status: event.target.value })}
                    >
                      <option value=''>Select Portal Status</option>
                      {portalStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {errors?.portal_status &&
                  errors?.portal_status.map((message) => (
                    <p className='help is-danger' key={message}>
                      {message}
                    </p>
                  ))}
              </div>
              <div className='columns mb-6'>
                <div className='field column'>
                  <label htmlFor='images' className='label'>
                    Organization logo
                    <div className='control'>
                      <DropzoneArea
                        showAlerts={false}
                        filesLimit={1}
                        maxFileSize={MEGA_BYTES_10}
                        initialFiles={formData?.logo_url ? [formData?.logo_url as string] : undefined}
                        onChange={(fileImages) => {
                          const logo = formData.logo && !fileImages[0] ? 'remove' : fileImages[0]
                          onChange({ ...formData, logo }, true)
                        }}
                        onDropRejected={() => {
                          setLogoErrors(['File is rejected. Max size is 10Mb'])
                        }}
                        onDrop={() => {
                          setLogoErrors([])
                        }}
                      />
                    </div>
                  </label>
                  <p className='help'>Accepts PNG, JPG, or JPEG files</p>
                  {(logoErrors || errors?.logo)?.map((message: string) => (
                    <p className='help is-danger' key={message.length}>
                      {message}
                    </p>
                  ))}
                </div>
                <div className='field column'>
                  <label htmlFor='images' className='label'>
                    Hero image
                    <div className='control'>
                      <DropzoneArea
                        showAlerts={false}
                        filesLimit={1}
                        maxFileSize={MEGA_BYTES_10}
                        initialFiles={formData?.login_image_url ? [formData?.login_image_url as string] : undefined}
                        onChange={(fileImages) => {
                          const loginImage = formData.login_image && !fileImages[0] ? 'remove' : fileImages[0]
                          onChange({ ...formData, login_image: loginImage }, true)
                        }}
                        onDropRejected={() => {
                          setHeroImageErrors(['File is rejected. Max size is 10Mb'])
                        }}
                        onDrop={() => {
                          setHeroImageErrors([])
                        }}
                      />
                    </div>
                  </label>
                  {(heroImageErrors || errors?.login_image)?.map((message: string) => (
                    <p className='help is-danger' key={message.length}>
                      {message}
                    </p>
                  ))}
                </div>
              </div>
              {formData.paid_by_organization && (
                <div className='field mb-6'>
                  <label>Main subscription ID</label>
                  <div className='control'>
                    <input
                      type='text'
                      className='input'
                      required
                      value={formData.main_subscription_id}
                      onChange={(event) => onChange({ ...formData, main_subscription_id: event.target.value })}
                    />
                  </div>
                  {errors?.main_subscription_id &&
                    errors?.main_subscription_id.map((message) => (
                      <p className='help is-danger' key={message}>
                        {message}
                      </p>
                    ))}
                </div>
              )}
            </>
          )}
          <div className='field has-text-centered'>
            {!isEdit && (
              <button type='submit' className='button is-primary'>
                Save
              </button>
            )}
            {!!errors && !!Object.values(errors).length && (
              <div className='has-text-danger mt-2'>{Phrases.FormError}</div>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}

export default OrganizationForm
